// PollReadService.js

/**
 * PollReadService.js
 *
 * Central place for Firestore poll vote reads.
 */

// Firebase
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Helper to check for non-empty string
const isNonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0;

/**
 * Gets a user's vote on a specific poll.
 * 
 * @param {string} pollNodeKey - The key of the poll node.
 * @param {string} userKey - The key of the user.
 * 
 * @returns {Promise<object|null>} The vote object or null if not found.
 */
export async function getUserPollVote(pollNodeKey, userKey) {

	// Validate inputs
	if (!isNonEmptyString(pollNodeKey) || !isNonEmptyString(userKey)) return null;

	// Reference to the vote document
	const voteRef = doc(db, collections.nodes, pollNodeKey, 'votes', userKey);

	// Fetch the document
	const snap = await getDoc(voteRef);

	// Return null if not exists
	if (!snap.exists()) return null;

	// Return vote data
	return { id: snap.id, ...snap.data() };
}

/**
 * Checks if a user has voted on a specific poll.
 * 
 * @param {string} pollNodeKey - The key of the poll node.
 * @param {string} userKey - The key of the user.
 * 
 * @returns {Promise<boolean>} True if the user has voted, false otherwise.
 */
export async function hasUserVotedOnPoll(pollNodeKey, userKey) {

	// Validate inputs
	const vote = await getUserPollVote(pollNodeKey, userKey);

	// Check if vote exists and matches pollNodeKey
	return Boolean(vote && vote.nodeKey === pollNodeKey);
}

/**
 * Lists all votes for a specific poll.
 * 
 * @param {string} pollNodeKey - The key of the poll node.
 * 
 * @returns {Promise<Array>} Array of vote objects.
 */
export async function listPollVotes(pollNodeKey) {

	// Validate input
	if (!isNonEmptyString(pollNodeKey)) return [];

	// Reference to votes subcollection
	const votesRef = collection(db, collections.nodes, pollNodeKey, 'votes');

	// Query for all votes on the poll
	const q = query(votesRef, where('nodeKey', '==', pollNodeKey));

	// Fetch documents
	const snapshot = await getDocs(q);

	// Map and return vote data
	return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
}

export async function getPollVoteCount(pollNodeKey) {

	// Validate input
	if (!isNonEmptyString(pollNodeKey)) return 0;

	// Get all votes
	const votes = await listPollVotes(pollNodeKey);

	// Return count
	return votes.length;
}
